import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-base-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './base-modal.component.html',
  styleUrls: ['./base-modal.component.scss'],
})
export class BaseModalComponent implements OnChanges, OnDestroy {
  @Input() open = false;
  @Input() title = 'Modal';
  @Input() showCloseButton = true;
  @Input() closeOnBackdrop = true;
  @Input() closeOnEscape = true;
  @Input() size: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '2xl-full-h' | 'full' = 'md';

  @Output() openChange = new EventEmitter<boolean>();
  @Output() closed = new EventEmitter<'button' | 'backdrop' | 'escape'>();
  /**
   * Emitted before the modal actually closes. The event payload exposes a `cancel()` callback
   * that, when invoked, prevents the close from proceeding — useful for unsaved-changes
   * guards. The parent is responsible for showing any confirmation UI; if the user later
   * confirms the close, the parent should set `[open]` to `false` (or unmount the modal).
   */
  @Output() beforeClose = new EventEmitter<{
    reason: 'button' | 'backdrop' | 'escape';
    cancel: () => void;
  }>();

  @ViewChild('panel') panelRef?: ElementRef<HTMLElement>;

  private previouslyFocused: HTMLElement | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      this.toggleBodyScroll(this.open);
      if (this.open) {
        this.captureFocusOrigin();
        this.focusPanel();
      } else {
        this.restoreFocus();
      }
    }
  }

  ngOnDestroy(): void {
    this.toggleBodyScroll(false);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open && this.closeOnEscape) {
      // If a child modal overlay exists inside this panel, a nested modal is open.
      // Don't close — let the child (innermost) modal handle escape.
      if (this.panelRef?.nativeElement?.querySelector('.modal-overlay')) {
        return;
      }
      this.close('escape');
    }
  }

  close(reason: 'button' | 'backdrop' | 'escape' = 'button'): void {
    if (!this.open) {
      return;
    }
    let cancelled = false;
    const cancel = () => {
      cancelled = true;
    };
    this.beforeClose.emit({ reason, cancel });
    if (cancelled) {
      return;
    }
    this.open = false;
    this.openChange.emit(false);
    this.closed.emit(reason);
    this.toggleBodyScroll(false);
    this.restoreFocus();
  }

  onBackdropClick(): void {
    if (this.closeOnBackdrop) {
      this.close('backdrop');
    }
  }

  private toggleBodyScroll(lock: boolean): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.body.classList.toggle('overflow-hidden', lock);
  }

  private captureFocusOrigin(): void {
    if (typeof document === 'undefined') {
      return;
    }
    this.previouslyFocused = (document.activeElement as HTMLElement) ?? null;
  }

  private focusPanel(): void {
    if (typeof document === 'undefined') {
      return;
    }
    setTimeout(() => this.panelRef?.nativeElement?.focus(), 0);
  }

  private restoreFocus(): void {
    const prev = this.previouslyFocused;
    this.previouslyFocused = null;
    prev?.focus?.();
  }
}
